/**
 * Data Explorer — the data as an org chart, read top-down.
 *
 *                        ┌───────────────┐
 *                        │ All Customers │
 *                        └──────┬───────┘
 *         ┌──────────────┬──────┴───────┬──────────────┐
 *     [4sightAI]      [AI4AP]       [AI4RTGS]     [No customer]
 *                        │
 *                ┌───────┴───────┐             ← under AI4AP, not centred
 *            [News360AI]     [SOCINT]            on the page
 *                                │
 *                        ┌───────┴───────┐
 *                     [Org]      [Software Engg]  ← the side menu's groups
 *                                        │
 *                                ┌───────┴───────┐
 *                            [Errors]        [Logs]   ← opens the rows
 *
 * EACH LEVEL IS ITS OWN ROW, centred on the card that opened it. Nesting a level
 * inside its parent's card made that column grow tall and flex-wrap pushed the
 * remaining siblings under the sub-tree, where they read as children of the
 * wrong node. A row instead carries an offset that puts the middle of its bus
 * under the middle of its parent — the org-chart reading — clamped at zero so a
 * branch near the left edge stays on screen.
 *
 * One node is open per level, deliberately. Branching everything at once gives a
 * chart nobody can read, and what is compared here is what sits inside one
 * thing, not two of them side by side.
 *
 * Records open in a DIALOG, not a sixth level: a row is a document, and a
 * document does not belong in a chart node.
 *
 * And the dialog does not imitate the screen that collection already has — it
 * MOUNTS IT. Opening `unit_master` renders the same UnitsList the side menu
 * routes to, with its own toolbar, filters, columns and actions, pointed at this
 * branch's project through `appId`. Nothing is reimplemented, so nothing can
 * drift. See collection-screens.ts.
 *
 * Collections with no such screen fall back to this file's own grid, reading
 * either the collection's list API (collection-apis.ts) or, failing that, the
 * explorer's generic endpoint.
 *
 * Read-only on both paths, and the caller's scope is enforced server-side either
 * way: the generic endpoint applies it directly, and `app_id` is intersected
 * with it by the native routers. Credential-shaped fields are redacted by name
 * on both — the server does it for the generic read, collection-apis.ts does it
 * for the native one.
 */
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from 'sharedUi/Dialog';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import SaasDataTable, {
  type SaasColumn,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import { deriveColumns } from './record-columns';
import { NATIVE_LISTS, fetchNativeRows } from './collection-apis';
import { COLLECTION_SCREENS } from './collection-screens';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import TreeHierarchy from '../../components/tree-hierarchy/tree-hierarchy';
import moduleIcon from './module-icons';
import {
  dataExplorerService,
  type ExplorerCollection,
  type ExplorerProject,
} from '../../services/data-explorer.service';
import './data-explorer.css';

/** The page size every list screen in the product opens on. */
const PAGE_SIZE = 10;

/** Where to send someone who wants the collection's own page.
 *
 *  Still useful even though the dialog now mounts that page: the embedded copy
 *  is scoped to one project and has no Create button, and a view or edit action
 *  inside it navigates away. This is the way out to the full, unscoped screen. */
const COLLECTION_ROUTES: Record<string, string> = {
  unit_master: '/units',
  district_master: '/districts',
  user_master: '/user-management',
  personnel_master: '/personnel',
  error_master: '/error-master',
  log_master: '/log-master',
  prompt_master: '/prompts',
  rag_master: '/rags',
  agent_master: '/agents',
  eval_suite: '/evals',
  value_sets_master: '/value-sets',
  feedback_master: '/feedback',
  notification_master: '/notification-masters',
  partner_master: '/partners',
  pipeline_master: '/pipelines',
  schedule_master: '/schedules',
};

/** Fields worth promoting to a row's headline, best first. */
const TITLE_FIELDS = ['name', 'jobName', 'displayName', 'label', 'key', 'code', 'errorCode',
  'logCode', 'unitCode', 'feedbackKey', 'agentCode', 'suiteCode', 'caseCode', 'promptId',
  'userId', 'title', 'scheduleName', 'ragCode', 'pipelineCode'];

const pick = (row: Record<string, unknown>, fields: string[]): string => {
  for (const f of fields) {
    const v = row[f];
    if (typeof v === 'string' && v.trim()) return v;
    if (typeof v === 'number') return String(v);
  }
  return '';
};

const fmtNum = (n: number) => n.toLocaleString('en-IN');

/** A customer, and the projects under it. The server calls this a partner,
 *  because that is the collection it comes from; everything a person reads here
 *  says customer. */
interface CustomerGroup { id: string; name: string; projects: ExplorerProject[]; records: number }
/** One of the three modules the platform dashboard already divides a project
 *  into — AI, Software Engineering, Unit Hierarchy.
 *
 *  "Module" rather than "area" or "capability": a reader should not have to be
 *  told what the word means, and this one is already in everybody's vocabulary
 *  for a part of a system that can be present or absent, set up or not. */
interface Module {
  key: string;
  label: string;
  description: string;
  unit: string;
  tone: string;
  /** How this module divides its contents, or null when it does not. */
  split: [string, string] | null;
  /** The masters in this module, each with whatever hangs under it. */
  masters: MasterGroup[];
  count: number;
  /** Masters that hold anything, out of all of them. Counted over MASTERS, not
   *  over every collection: the next level down shows masters, so "6 of 8" has
   *  to describe the row you are about to open, not a number nothing on screen
   *  adds up to. */
  configured: number;
  total: number;
}

/** A thing somebody set up, and what it has since produced.
 *
 *  Runs, versions and occurrences used to sit beside their own parents as
 *  equals — eighteen unrelated tables where there were really six things and
 *  their histories. The server says which collection each one hangs under; this
 *  only assembles it. */
interface MasterGroup {
  collection: ExplorerCollection;
  children: ExplorerCollection[];
  /** The master plus everything under it. */
  count: number;
}

/* ------------------------------------------------------------ records dialog */

function RecordsDialog({ project, collection, onHide }: {
  project: ExplorerProject;
  collection: ExplorerCollection;
  onHide: () => void;
}) {
  const navigate = useAppNavigate();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  /* Sorting and per-column filters exist only on the native path: the generic
     reader takes neither, and a grid that offers a control doing nothing is
     worse than one that does not offer it. */
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [colFilters, setColFilters] = useState<Record<string, unknown>>({});
  /** The one record being read in full. Replaces the list rather than stacking a
   *  second dialog on top of it — a dialog over a dialog is a trap to get out of. */
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setFirst(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  /* The collection's own list endpoint, where it has one that takes an app_id.
     Same API the side menu calls, same server-side joins and computed fields —
     just pointed at the project this branch of the chart is about. */
  /* The collection's real screen, where the product has one. Mounted as-is —
     see collection-screens.ts — so there is nothing here to drift out of step
     with it. The generic reader below is the fallback, not the default. */
  const Screen = COLLECTION_SCREENS[collection.collection];

  const native = NATIVE_LISTS[collection.collection];
  const filtersParam = native?.filters ? buildColumnFiltersParam(colFilters) : undefined;

  const q = useQuery({
    queryKey: ['dx-records', project._id, collection.collection, debounced, field, value,
      first, pageSize, sortField, sortOrder, filtersParam],
    queryFn: async () => {
      if (native) {
        const { items, total: n } = await fetchNativeRows(native, {
          appId: project._id,
          page: Math.floor(first / pageSize) + 1,
          pageSize,
          search: debounced || undefined,
          sortBy: sortField || undefined,
          sortOrder: sortOrder === -1 ? 'desc' : 'asc',
          filters: filtersParam,
        });
        return { items, filters: [], total: n };
      }
      return dataExplorerService.records({
        appId: project._id,
        collection: collection.collection,
        search: debounced || undefined,
        field: field || undefined,
        value: field && value ? value : undefined,
        page: Math.floor(first / pageSize) + 1,
        page_size: pageSize,
      });
    },
    enabled: !Screen,
    staleTime: 20_000,
  });

  const rows = useMemo(() => q.data?.items ?? [], [q.data]);
  const filters = q.data?.filters ?? [];
  const total = q.data?.total ?? collection.count;
  const activeFilter = filters.find((f) => f.field === field);
  const route = COLLECTION_ROUTES[collection.collection];

  /* The grid's own columns, read off the rows — plus the one action a read-only
     screen has. See record-columns.ts for what is dropped and why. */
  const columns = useMemo<SaasColumn<Record<string, unknown>>[]>(() => {
    const derived = deriveColumns(rows);
    // Nothing to read a shape off: an empty collection still needs a table for
    // its empty message to sit in, and a lone "Actions" header over no rows
    // reads like the grid failed rather than like there is nothing here.
    if (!derived.length) {
      return [{ field: 'record', header: collection.label, hideable: false, body: () => '' }];
    }
    return [...derived.map((c) => ({
      ...c,
      // The native routers sort and filter per column exactly as they do for
      // their own screen; the generic reader does neither.
      sortable: !!native?.sort,
      filter: !!native?.filters,
      filterType: 'text' as const,
    })), {
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (row) => (
        <div className="flex gap-1 justify-center">
          <button
            type="button"
            aria-label="View"
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            onClick={(e) => { e.stopPropagation(); setDetail(row); }}
            data-testid="DataExplorer.Action.View"
          >
            <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.05rem' }} />
          </button>
        </div>
      ),
    }];
  }, [rows, collection.label, native]);

  const header = detail ? (
    <span className="dx-dlg-head">
      <button type="button" className="dx-dlg-back" onClick={() => setDetail(null)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" width="14">
          <path d="M19 12H5M11 18l-6-6 6-6" />
        </svg>
        {collection.label}
      </button>
      {pick(detail, TITLE_FIELDS) || 'Record'}
    </span>
  ) : (
    <span className="dx-dlg-head">
      {collection.label}
      <span className="dx-dlg-sub">{project.name} · {fmtNum(collection.count)} records</span>
    </span>
  );

  return (
    <Dialog
      visible
      onHide={onHide}
      header={header}
      className={Screen ? 'dx-dlg-screen' : undefined}
      /* A mounted screen gets a definite height to measure against — see the
         note on .dx-dlg-screen. The generic grid sizes to its own content. */
      style={Screen
        ? { width: 1340, maxWidth: '96vw', height: '88vh' }
        : { width: 1180, maxWidth: '96vw' }}
      contentStyle={Screen
        ? { padding: '0 1.25rem 1rem', overflow: 'hidden' }
        : { minHeight: 420 }}
      modal
      dismissableMask
      data-testid="DataExplorer.Records"
    >
      {Screen ? (
        <div className="dx-screen">
          <Suspense fallback={<div className="dx-inline-empty">Opening {collection.label}…</div>}>
            <Screen embedded appId={project._id} />
          </Suspense>
        </div>
      ) : detail ? (
        <div className="dx-kv">
          {Object.entries(detail).map(([k, v]) => (
            <div className="dx-kv-row" key={k}>
              <span className="k">{k}</span>
              <span className="v">
                {typeof v === 'object' && v !== null
                  ? <pre>{JSON.stringify(v, null, 2)}</pre>
                  : String(v ?? '—')}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="dx-grid">
          <SaasDataTable
            tableId={`dx-${collection.collection}`}
            columns={columns}
            data={rows}
            loading={q.isFetching}
            dataKey="_id"
            emptyMessage={debounced || value ? 'Nothing matches this search.' : 'This collection is empty.'}
            first={first}
            rows={pageSize}
            totalRecords={total}
            onPage={(e: { first: number; rows: number }) => { setFirst(e.first); setPageSize(e.rows); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={(e: { sortField: string | null; sortOrder: 1 | -1 | null }) => {
              setSortField(e.sortField);
              setSortOrder(e.sortOrder);
              setFirst(0);
            }}
            filters={colFilters}
            onFilter={(e: { filters: Record<string, unknown> }) => {
              setColFilters(e.filters || {});
              setFirst(0);
            }}
            globalSearch={search}
            onGlobalSearchChange={setSearch}
            globalSearchPlaceholder={`Search ${collection.label.toLowerCase()}...`}
            fillHeight={false}
            rowClassName={() => 'cursor-pointer'}
            onRowClick={(row) => setDetail(row)}
            testId="DataExplorer.Grid"
            toolbarStart={filters.length > 0 && (
              <>
                <Dropdown
                  value={field}
                  options={[{ label: 'Filter by field', value: '' },
                    ...filters.map((f) => ({ label: f.field, value: f.field }))]}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Filter by field"
                  showClear={!!field}
                  style={{ minWidth: 170 }}
                  onChange={(e: { value: string }) => { setField(e.value || ''); setValue(''); setFirst(0); }}
                  data-testid="DataExplorer.Filter.Field"
                />
                {activeFilter && (
                  <Dropdown
                    value={value}
                    options={[{ label: 'Any value', value: '' },
                      ...activeFilter.values.map((v) => ({ label: v, value: v }))]}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Any value"
                    showClear={!!value}
                    style={{ minWidth: 170 }}
                    onChange={(e: { value: string }) => { setValue(e.value || ''); setFirst(0); }}
                    data-testid="DataExplorer.Filter.Value"
                  />
                )}
              </>
            )}
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {fmtNum(total)} {total === 1 ? 'record' : 'records'}
                </span>
                {route && (
                  <Button
                    label="Open full screen"
                    icon="pi pi-external-link"
                    size="small"
                    outlined
                    onClick={() => navigate(route)}
                    data-testid="DataExplorer.OpenScreen"
                  />
                )}
              </>
            }
          />
        </div>
      )}
    </Dialog>
  );
}

/* --------------------------------------------------------------------- page */

const DataExplorer = () => {
  const navigate = useAppNavigate();
  const [openCustomerId, setOpenCustomerId] = useState<string | null>(null);
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [openMaster, setOpenMaster] = useState<string | null>(null);
  const [opened, setOpened] = useState<{ project: ExplorerProject; collection: ExplorerCollection } | null>(null);

  const projectsQ = useQuery({
    queryKey: ['dx-projects'],
    queryFn: dataExplorerService.projects,
    staleTime: 60_000,
  });

  // Memoised: a fresh [] each render would re-run the groupings on every paint.
  const projects = useMemo(() => projectsQ.data ?? [], [projectsQ.data]);

  /** Projects grouped under their customer. A project with none gets its own
   *  bucket rather than being hidden — standing alone is a real state. */
  const customers: CustomerGroup[] = useMemo(() => {
    const byId = new Map<string, CustomerGroup>();
    projects.forEach((p) => {
      const id = p.partnerId || '__none__';
      const name = p.partnerName || 'No customer';
      if (!byId.has(id)) byId.set(id, { id, name, projects: [], records: 0 });
      const g = byId.get(id)!;
      g.projects.push(p);
      g.records += p.records;
    });
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  const openCustomer = customers.find((g) => g.id === openCustomerId) ?? null;

  /* WHERE THE CHART STARTS depends on what this login can reach.
   *
   * A platform admin sees every customer, so the tree hangs off "All Customers".
   * A customer admin reaches one customer: that customer IS the root, and the
   * chart opens on its projects — a row of one above it said nothing. A project
   * login reaches one project, so the project is the root and the chart opens
   * straight into its modules.
   *
   * Read off the DATA rather than off the permission, which gets the same answer
   * and one more besides: a platform admin of a single-customer deployment also
   * stops being shown a fan-out of one. */
  const soleCustomer = customers.length === 1 ? customers[0] : null;
  const soleProject = soleCustomer && soleCustomer.projects.length === 1
    ? soleCustomer.projects[0] : null;

  /* A root is not a card you can click, so anything it stands for must be opened
   * on the viewer's behalf — otherwise the level below it never renders. */
  useEffect(() => {
    if (soleCustomer && openCustomerId !== soleCustomer.id) setOpenCustomerId(soleCustomer.id);
  }, [soleCustomer, openCustomerId]);
  useEffect(() => {
    if (soleProject && openProjectId !== soleProject._id) setOpenProjectId(soleProject._id);
  }, [soleProject, openProjectId]);
  const openProject = openCustomer?.projects.find((p) => p._id === openProjectId) ?? null;

  /* Collections of the open project, and the areas they fall into. Fetched only
     once a project is open — nothing below an unopened branch is loaded. */
  const collectionsQ = useQuery({
    queryKey: ['dx-collections', openProject?._id],
    queryFn: () => dataExplorerService.collections(openProject!._id),
    enabled: !!openProject,
    staleTime: 60_000,
  });

  /* Assembled in the order the server sent them: areas in the order a project
     reads best, masters in theirs, and a master's own runs after it. None of it
     is a volume ranking — sorting by row count puts the executions before the
     prompt that produced them. */
  const categories: Module[] = useMemo(() => {
    const byKey = new Map<string, Module>();
    const masters = new Map<string, MasterGroup>();

    (collectionsQ.data ?? []).forEach((c) => {
      if (!byKey.has(c.module)) {
        byKey.set(c.module, {
          key: c.module,
          label: c.moduleLabel,
          description: c.moduleDescription,
          unit: c.moduleUnit,
          split: c.moduleSplit,
          tone: c.tone,
          masters: [],
          count: 0,
          configured: 0,
          total: 0,
        });
      }
      const module = byKey.get(c.module)!;
      module.count += c.count;

      if (c.master) {
        // Its master always arrives first — the server lists a master ahead of
        // what hangs under it — but a child whose master is missing would
        // otherwise be dropped silently, so it is promoted instead.
        const parent = masters.get(c.master);
        if (parent) {
          parent.children.push(c);
          parent.count += c.count;
          return;
        }
      }
      const group: MasterGroup = { collection: c, children: [], count: c.count };
      masters.set(c.collection, group);
      module.masters.push(group);
    });

    // Counted once the masters are assembled, not while they are being built.
    return [...byKey.values()].map((m) => ({
      ...m,
      configured: m.masters.filter((g) => g.count > 0).length,
      total: m.masters.length,
    }));
  }, [collectionsQ.data]);

  const category = openCategory ? categories.find((g) => g.key === openCategory) ?? null : null;
  /* A split module's two halves: what was set up, and what running it
     produced. Which side a collection is on is the server's answer, carried on
     the row itself — inferring it from "has a parent" put RAG versions among
     the executions and permission sets among the transactions. */
  interface Half { key: string; label: string; collections: ExplorerCollection[]; count: number }

  const halves: Half[] = useMemo(() => {
    if (!category?.split) return [];
    const [setUpLabel, producedLabel] = category.split;
    const of = (side: 'masters' | 'runs') => category.masters
      .flatMap((m) => [m.collection, ...m.children])
      .filter((c) => c.half === side);
    return [
      { key: 'masters', label: setUpLabel, collections: of('masters') },
      { key: 'runs', label: producedLabel, collections: of('runs') },
    ]
      .map((h) => ({ ...h, count: h.collections.filter((c) => c.count > 0).length }))
      .filter((h) => h.collections.length > 0);
  }, [category]);

  const openHalf = halves.find((h) => h.key === openMaster) ?? null;

  const master = openMaster
    ? category?.masters.find((m) => m.collection.collection === openMaster) ?? null
    : null;


  /* The chart's four levels. Each one says what its cards are, what they look
     like and what opening one means; the chart owns the geometry and the wiring,
     and knows nothing about customers or collections. */
  /* Levels above the root are dropped, not hidden: the root already IS that
     node, and leaving a one-card row under it would say the same thing twice. */
  const allLevels = [
    {
      id: 'customers',
      items: customers,
      keyOf: (g: CustomerGroup) => g.id,
      openKey: openCustomerId,
      onToggle: (key: string) => {
        setOpenCustomerId((cur) => (cur === key ? null : key));
        setOpenProjectId(null);
        setOpenCategory(null);
        setOpenMaster(null);
      },
      cardClassName: () => 'dx-node-customer th-tone-teal',
      cardTestId: (g: CustomerGroup) => `DataExplorer.Customer.${g.id}`,
      testId: 'DataExplorer.Row.Customers',
      renderCard: (g: CustomerGroup) => (
        <>
          <span className="th-node-head">
            <span className="th-badge">{g.name.slice(0, 2).toUpperCase()}</span>
            <span className="th-names">
              <span className="th-title">{g.name}</span>
            </span>
          </span>
          <span className="th-foot">
            <span className="th-stat">
              <span className="th-figure">{g.projects.length}</span>
              <span className="th-unit">project{g.projects.length === 1 ? '' : 's'}</span>
            </span>
          </span>
        </>
      ),
    },
    {
      id: 'projects',
      items: openCustomer?.projects ?? [],
      keyOf: (p: ExplorerProject) => p._id,
      openKey: openProjectId,
      onToggle: (key: string) => {
        setOpenProjectId((cur) => (cur === key ? null : key));
        setOpenCategory(null);
        setOpenMaster(null);
      },
      cardClassName: () => 'dx-node-proj th-tone-indigo',
      cardTestId: (p: ExplorerProject) => `DataExplorer.Project.${p.appCode ?? p._id}`,
      testId: 'DataExplorer.Row.Projects',
      renderCard: (p: ExplorerProject) => (
        <>
          <span className="th-node-head">
            <span className="th-badge">{(p.appCode || p.name || '?').slice(0, 2).toUpperCase()}</span>
            <span className="th-names">
              <span className="th-title">{p.name}</span>
              <span className="th-meta">{p.appCode}</span>
            </span>
          </span>
          {/* What the project HAS, not how much has run through it. A row count
              at this height compares unlike things — one project's audit trail
              against another's model definitions — and the biggest number
              always belonged to whoever logged the most. */}
          <span className="th-desc">
            {p.collections} collection{p.collections === 1 ? '' : 's'} in use
          </span>
          <span className="th-foot">
            <span className="th-stat">
              <span className="th-figure">{p.modules}</span>
              <span className="th-unit">module{p.modules === 1 ? '' : 's'}</span>
            </span>
          </span>
        </>
      ),
    },
    {
      id: 'modules',
      items: categories,
      keyOf: (g: Module) => g.key,
      openKey: openCategory,
      onToggle: (key: string) => {
        setOpenCategory((cur) => (cur === key ? null : key));
        setOpenMaster(null);
      },
      cardClassName: (g: Module) => `dx-node-cat th-tone-${g.tone}`,
      cardTestId: (g: Module) => `DataExplorer.Module.${g.key}`,
      // Nothing configured, so nothing to open into.
      cardDisabled: (g: Module) => g.configured === 0,
      cardTitle: (g: Module) =>
        `${g.label}: ${g.configured} of ${g.total} collections hold data`,
      testId: 'DataExplorer.Row.Modules',
      loading: collectionsQ.isLoading,
      loadingMessage: 'Working out what this project uses…',
      emptyMessage: 'This project holds no data yet.',
      renderCard: (g: Module) => (
        <>
          <span className="th-node-head">
            <span className="th-icon" aria-hidden="true">
              <i className={moduleIcon(g.key)} />
            </span>
            <span className="th-names">
              <span className="th-title">{g.label}</span>
              {/* Not a bar. One area's total says nothing against another's —
                  the audit trail outweighs everything and always will, which
                  told you only that logs are chatty. What is worth knowing
                  before clicking is how much of this is switched on. */}
              <span className="th-meta">
                {g.total} collection{g.total === 1 ? '' : 's'}
              </span>
            </span>
          </span>
          <span className="th-desc">{g.description}</span>
          <span className="th-foot">
            {/* "11 of 14 configured" made people ask what configured meant,
                and fairly: it is not a setting anybody turned on. It counts the
                collections in this module that actually hold something, which
                is the thing worth knowing before you open it. Said plainly, it
                needs no explaining. */}
            <span className="th-stat">
              <span className="th-figure">{g.configured}</span>
              <span className="th-unit">with data</span>
            </span>
            {/* The module keeps the fuller words: it now sits in the footer
                rather than over the title, so there is room for them, and
                "On/Off" reads like a switch somebody could flick. */}
            <span className={`th-pill${g.configured > 0 ? ' is-live' : ''}`}>
              {g.configured > 0 ? 'In use' : 'No data'}
            </span>
          </span>
        </>
      ),
    },
    {
      id: 'masters',
      /* Where a module splits, this level is the two halves rather than the
         masters themselves — eighteen cards in one row read as eighteen
         unrelated tables. Unit Hierarchy does not split, so it still lists its
         masters straight away. */
      items: category?.split ? halves : (category?.masters ?? []),
      keyOf: (x: Half | MasterGroup) =>
        ('key' in x ? x.key : x.collection.collection),
      openKey: openMaster,
      onToggle: (key: string, x: Half | MasterGroup) => {
        /* A master with nothing under it IS a leaf: expanding it would open a
           row holding only itself. So it goes straight to its records. A half
           always expands — it is nothing but a way in. */
        if (!('key' in x) && !x.children.length) {
          setOpenMaster(null);
          if (openProject) setOpened({ project: openProject, collection: x.collection });
          return;
        }
        setOpenMaster((cur) => (cur === key ? null : key));
      },
      cardClassName: (x: Half | MasterGroup) => {
        const empty = 'key' in x ? x.count === 0 : x.count === 0;
        return `dx-node-col th-tone-${category?.tone ?? 'slate'}${empty ? ' is-empty' : ''}`;
      },
      cardTestId: (x: Half | MasterGroup) =>
        ('key' in x
          ? `DataExplorer.Half.${x.key}`
          : `DataExplorer.Master.${x.collection.collection}`),
      // Counted over the subtree, so a catalogue whose own rows are empty but
      // whose children are not stays open-able.
      cardDisabled: (x: Half | MasterGroup) => x.count === 0,
      cardTitle: (x: Half | MasterGroup) => ('key' in x
        ? `${x.label}: ${x.collections.length} collections`
        : (x.children.length
          ? `${x.collection.collection} — opens ${x.children.length} related collection`
            + `${x.children.length === 1 ? '' : 's'}`
          : x.collection.collection)),
      testId: 'DataExplorer.Row.Masters',
      renderCard: (x: Half | MasterGroup) => {
        if ('key' in x) {
          return (
            <>
              <span className="th-title">{x.label}</span>
              <span className="th-desc">
                {x.key === 'set-up'
                  ? 'What has been configured in this module.'
                  : 'What has come out of running it.'}
              </span>
              <span className="th-foot">
                <span className="th-stat">
                  <span className="th-figure">{x.collections.length}</span>
                  <span className="th-unit">{x.label.toLowerCase()}</span>
                </span>
                <span className={`th-pill${x.count > 0 ? ' is-live' : ''}`}>
                  {x.count > 0 ? `${x.count} active` : 'Empty'}
                </span>
              </span>
            </>
          );
        }
        const c = x.collection;
        return (
          <>
            <span className="th-title">{c.label}</span>
            <span className="th-desc">{c.description || c.collection}</span>
            <span className="th-foot">
              <span className="th-stat">
                <span className="th-figure">{fmtNum(c.count)}</span>
                <span className="th-unit">{c.count === 1 ? c.unitOne : c.unit}</span>
              </span>
              {x.children.length > 0 && (
                <span className="th-more">
                  +{x.children.length} related
                </span>
              )}
              {/* The subtree, not the card's own rows: a catalogue with no
                  entries of its own but 17 under it is not empty. */}
              <span className={`th-pill${x.count > 0 ? ' is-live' : ''}`}>
                {x.count > 0 ? 'Active' : 'Empty'}
              </span>
            </span>
          </>
        );
      },
    },
    {
      id: 'runs',
      /* An open half lists its own collections. An open master leads its own
         row: without it there is no way to read the prompts once you have
         opened Prompts, only the runs they produced — the wrong half of the
         answer. */
      items: openHalf
        ? openHalf.collections
        : (master ? [master.collection, ...master.children] : []),
      keyOf: (c: ExplorerCollection) => c.collection,
      // A leaf: it opens a dialog, not another row.
      onSelect: (c: ExplorerCollection) => {
        if (openProject) setOpened({ project: openProject, collection: c });
      },
      cardClassName: (c: ExplorerCollection) =>
        `dx-node-col th-tone-${category?.tone ?? 'slate'}${c.count === 0 ? ' is-empty' : ''}`,
      cardTestId: (c: ExplorerCollection) => `DataExplorer.Collection.${c.collection}`,
      cardTitle: (c: ExplorerCollection) => c.collection,
      // No rows, so the dialog would open on an empty table.
      cardDisabled: (c: ExplorerCollection) => c.count === 0,
      testId: 'DataExplorer.Row.Runs',
      renderCard: (c: ExplorerCollection) => (
        <>
          <span className="th-title">{c.label}</span>
          <span className="th-desc">{c.description || c.collection}</span>
          <span className="th-foot">
            <span className="th-stat">
              <span className="th-figure">{fmtNum(c.count)}</span>
              <span className="th-unit">{c.count === 1 ? c.unitOne : c.unit}</span>
            </span>
            <span className={`th-pill${c.count > 0 ? ' is-live' : ''}`}>
              {c.count > 0 ? 'Active' : 'Empty'}
            </span>
          </span>
        </>
      ),
    },
  ];
  const levels = allLevels.slice(soleProject ? 2 : soleCustomer ? 1 : 0);

  return (
    <div className="dx-root" data-testid="SCR-DataExplorer">
      <div className="dx-head">
        <button className="dx-back" type="button" onClick={() => navigate('/dashboard')} aria-label="Back to dashboard">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" width="16">
            <path d="M19 12H5M11 18l-6-6 6-6" />
          </svg>
        </button>
        <div className="dx-head-main">
          <h1>PROJECT-Data Hierarchy</h1>
          <p className="dx-lede">
            What each project is built from — the modules it uses, what has been
            configured in each, and what those have produced. You see the
            customers and projects your account can reach.
          </p>
        </div>
      </div>

      {projectsQ.isLoading ? (
        <div className="dx-empty">Counting…</div>
      ) : (
        <TreeHierarchy
          testId="DataExplorer.Chart"
          levels={levels}
          /* The root names whatever this login sits at the top of. */
          root={(
            <div className="th-node-root">
              <span className="th-eyebrow">
                {soleProject ? 'Project' : soleCustomer ? 'Customer' : 'Platform'}
              </span>
              <span className="th-title">
                {soleProject ? soleProject.name
                  : soleCustomer ? soleCustomer.name
                    : 'All Customers'}
              </span>
              <span className="th-meta">
                {soleProject ? (
                  <>{soleProject.modules} module{soleProject.modules === 1 ? '' : 's'}</>
                ) : soleCustomer ? (
                  <>
                    {soleCustomer.projects.length} project
                    {soleCustomer.projects.length === 1 ? '' : 's'}
                  </>
                ) : (
                  <>{customers.length} customers · {projects.length} projects</>
                )}
              </span>
            </div>
          )}
        />
      )}

      {opened && (
        <RecordsDialog
          project={opened.project}
          collection={opened.collection}
          onHide={() => setOpened(null)}
        />
      )}
    </div>
  );
};

export default DataExplorer;
